// Types for the isolated "Decision to make a 3rd level bid" data layer (feature 013).
// The session items are Problem-shaped (the server DTO mirrors the reused practice item), so
// the reused practice page/hook render them without modification.
import type { Problem } from './problems';

/**
 * Wire shape of GET /api/third-level-bid-decision/{candidates,by-id}. Uses `items` (this
 * feature has no "problems"); the api module adapts it to the reused SessionResponse.
 */
export interface ThirdLevelBidSessionResponse {
  items: Problem[];
  totalAvailable: number;
}

/**
 * Opener-based practice sub-mode (Session 2026-06-08). `overcalled` = an opponent opened;
 * `precision` = Jeff/partner opened with one of the six Precision openings (requires
 * `openingBid`); `other` = Jeff/partner opened otherwise.
 */
export type ThirdLevelSubMode = 'overcalled' | 'precision' | 'other';

/** The six Precision opening-bid radio values; `1H,1S` is the combined-major option (FR-023). */
export type ThirdLevelOpeningBid = '1C' | '1D' | '1H,1S' | '1NT' | '2C' | '2D';

/**
 * Editor-mode data-category filter (Session 2026-06-15, FR-028). `Unanswered` is the default.
 * "Answered" is per-editor; "Assigned" is global. `IncorrectlyAnswered` (Session 2026-06-24) is a
 * per-editor subset of "Answered" — hands whose stored answer ≠ Jeff's actual call. Only sent for
 * Admin/Editor callers; the value matches the server `ThirdLevelEditorCategory` enum name.
 */
export type ThirdLevelEditorCategory =
  | 'All' | 'Answered' | 'Unanswered' | 'IncorrectlyAnswered' | 'Assigned' | 'Unassigned';

/** The ordered list of categories for the editor dropdown (FR-028); default is `Unanswered`. */
export const THIRD_LEVEL_EDITOR_CATEGORIES: ThirdLevelEditorCategory[] =
  ['All', 'Answered', 'Unanswered', 'IncorrectlyAnswered', 'Assigned', 'Unassigned'];

/** Human-friendly dropdown labels (the enum-name values are not all spaced). */
export const THIRD_LEVEL_EDITOR_CATEGORY_LABELS: Record<ThirdLevelEditorCategory, string> = {
  All: 'All',
  Answered: 'Answered',
  Unanswered: 'Unanswered',
  IncorrectlyAnswered: 'Incorrectly Answered',
  Assigned: 'Assigned',
  Unassigned: 'Unassigned',
};

export interface ThirdLevelEditorAssignDifficultyResponse {
  tableResultId: number;
  difficultyLevel: number;
}

export interface ThirdLevelEditorAnswerResponse {
  tableResultId: number;
  answerBid: string;
}

export interface SubmitThirdLevelAnswerRequest {
  bid: string;
}

export interface SubmitThirdLevelAnswerResponse {
  isCorrect: boolean;
  correctBid: string;
  expertName: string;
  expertComment: string;
  /**
   * Feature 027 — the AI engine's reasoning for the decision-turn call, present ONLY where the
   * engine predicted the call Jeff actually made and the hand carries no comment of Jeff's own.
   * Every hand on this surface is a downloaded tournament hand, so the AI panel always applies
   * here: null simply means "Information not available".
   */
  aiComment?: string | null;
}
